import { readContract, signTypedData } from "wagmi/actions";
import { nonStandardERC20Abi } from "./abis/nonStandardERC20Abi";
import {
  getPermitDomain,
  Permit1Details,
  Permit2Details,
  PERMIT_ONE_TYPES_DAI_LIKE,
  PERMIT_ONE_TYPES_STANDARD,
} from "./permitUtils";
import { toHex } from "viem";
import { PermitDomain, TokenData } from "../store/global/getTokenMetadata";
import { customERC20NoncesABI } from "./abis/erc20NonceAbis";
import { Token } from "../store/global/TokenEnum";
import { DAI_LIKE_PERMIT1_TOKENS } from "../config/consts";

export interface Permit1Signature {
  type: "Permit1";
  signature: `0x${string}`;
  deadline: number;
}

export interface Permit1Params {
  tokenAddress: `0x${string}`;
  owner: `0x${string}`;
  spender: `0x${string}`;
  value: bigint;
  nonce: bigint;
  deadline: number;
}

export interface ApproveViaPermit1Params {
  tokenAddress: `0x${string}`;
  owner: `0x${string}`;
  spender: `0x${string}`;
  value: bigint;
  nonce: bigint;
  deadline: number;
  chainId: number;
  tokenData: TokenData;
}

type ReadContractFunc = typeof readContract;
type PreparedReadConfig = Parameters<ReadContractFunc>[0];

export const getPermit1Nonce = async (tokenAddress: `0x${string}`, userAddress: `0x${string}`) => {
  /**
   * Reference: https://bebop-dex.atlassian.net/wiki/spaces/BEBOP/pages/124485633/Permit+Permit2#Frontend---Permit-integration
   * There are 4 possible functions that can be used to get the nonce: nonces, _nonces, nonce, getNonce
   *  We prepare the configs in order to preserve the wagmi type safety (partially - return type is lost)
   *  and we try them in order until we get a nonce back.
   */
  //
  const methods = ["nonces", "_nonces", "nonce", "getNonce"];
  const generateConfigs = (): Array<PreparedReadConfig> => {
    return methods.map(method => {
      const preparedReadConfig: PreparedReadConfig = {
        address: tokenAddress,
        abi: customERC20NoncesABI,
        functionName: method,
        args: [userAddress],
      };
      return preparedReadConfig;
    });
  };
  const configs = generateConfigs();
  for (const config of configs) {
    try {
      const res = await readContract(config);
      if (res !== undefined) {
        return res as bigint;
      }
    } catch (e) {
      //noop
    }
  }
  console.error(
    `Could not get nonce for token ${tokenAddress} and user ${userAddress} after trying all methods.`
  );
};

const getTokenNameFromContract = async (tokenAddress: `0x${string}`) => {
  return await readContract({
    address: tokenAddress,
    abi: nonStandardERC20Abi,
    functionName: "name",
  });
};

const getPermit1DomainVersion = async (tokenAddress: `0x${string}`) => {
  return await readContract({
    address: tokenAddress,
    abi: nonStandardERC20Abi,
    functionName: "version",
  });
};

export const getPermit1Domain = async (
  chainId: number,
  tokenAddress: `0x${string}`,
  tokenName: string,
  permitDomain: PermitDomain
) => {
  let domainVersion = "1"; // Defaulting to 1 in case this is a non-standard property on other (non-USDC) ERC-20 contracts
  try {
    domainVersion = await getPermit1DomainVersion(tokenAddress);
    console.log(`Inferring domain version from contract: ${domainVersion}`);
  } catch (e) {
    console.error(
      `Could not get domain version from contract. Will default to ${domainVersion}`,
      e
    );
  }

  if (permitDomain === PermitDomain.WITH_VERSION_AND_SALT) {
    return {
      name: tokenName,
      version: domainVersion,
      salt: toHex(chainId, { size: 32 }),
      verifyingContract: tokenAddress,
    } as const;
  }
  if (permitDomain === PermitDomain.WITH_CHAINID) {
    return {
      name: tokenName,
      chainId: Number(chainId),
      verifyingContract: tokenAddress,
    } as const;
  }
  if (permitDomain === PermitDomain.WITH_VERSION_AND_CHAINID) {
    return {
      name: tokenName,
      version: domainVersion,
      chainId: Number(chainId),
      verifyingContract: tokenAddress,
    } as const;
  }
  // Only for GRT on Ethereum (for now)
  if (permitDomain === PermitDomain.WITH_VERSION_AND_CHAINID_AND_SALT) {
    return {
      name: tokenName,
      version: domainVersion,
      chainId: Number(chainId),
      verifyingContract: tokenAddress,
      salt: "0x51f3d585afe6dfeb2af01bba0889a36c1db03beec88c6a4d0c53817069026afa", // Random hardcoded magic salt
    } as const;
  }
};

export const getPermit1Message = (params: Permit1Params) => {
  return {
    owner: params.owner,
    spender: params.spender,
    value: BigInt(params.value),
    nonce: params.nonce,
    deadline: BigInt(params.deadline),
  } as const;
};

export const getPermit1DaiLikeMessage = (params: Permit1Params) => {
  return {
    holder: params.owner,
    spender: params.spender,
    nonce: params.nonce,
    expiry: BigInt(params.deadline),
    allowed: true,
  } as const;
};

export const approveViaPermit1 = async (
  params: ApproveViaPermit1Params
): Promise<Permit1Signature> => {
  console.log(`APPROVING VIA PERMIT1`);
  const tokenName = await getTokenNameFromContract(params.tokenAddress);
  const permit1Domain = await getPermit1Domain(
    params.chainId,
    params.tokenAddress,
    tokenName,
    params.tokenData.permitDomain
  );
  const permit1Message = getPermit1Message(params);
  console.log(`Permit1 Domain:`, permit1Domain);
  console.log(`Permit1 Message:`, permit1Message);
  type PreparedSignTypedDataConfig = Parameters<typeof signTypedData>[0];
  const types = {
    ...getPermitDomain(params.tokenData.permitDomain),
    ...PERMIT_ONE_TYPES_STANDARD,
  } as const;
  const STANDARD_CONFIG: PreparedSignTypedDataConfig = {
    domain: permit1Domain,
    types: types,
    message: permit1Message,
    primaryType: "Permit" as const,
  };
  // For Dai-Like tokens we need a different signTyped data
  const daiTypes = {
    ...PERMIT_ONE_TYPES_DAI_LIKE,
    ...getPermitDomain(params.tokenData.permitDomain),
  } as const;
  const DAI_LIKE_CONFIG: PreparedSignTypedDataConfig = {
    domain: permit1Domain,
    types: daiTypes,
    message: getPermit1DaiLikeMessage(params),
    primaryType: "Permit" as const,
  };
  const isStandardConfig = !DAI_LIKE_PERMIT1_TOKENS.includes(params.tokenData.ticker as Token);
  const config = isStandardConfig ? STANDARD_CONFIG : DAI_LIKE_CONFIG;
  const signature = await signTypedData(config);
  return {
    type: "Permit1",
    signature,
    deadline: params.deadline,
  };
};

// Type narrower for use in other files
export const isPermit1Signature = (
  signature: Permit1Details | Permit2Details
): signature is Permit1Details => {
  return !Array.isArray((signature as Permit2Details).nonces);
};
